import { useField } from '@unform/core';
import React, {
  ChangeEvent,
  InputHTMLAttributes,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  ContainerButton,
  ContainerInput,
  ContainerMidia,
  Input,
  Button,
  ContainerContainerMidia,
} from './styles';

import noMidiaDefault from '../../assets/no-midia.svg';
import cameraImage from './assets/camera.svg';
import trashImage from './assets/trash.svg';

import { api } from '../../services/api';
import { useToast } from '../../hooks/toast';
import { useAuth } from '../../hooks/auth';
import { useModal } from '../../hooks/modal';
import { CropImageModal } from '../../modais/cropImage';
import { Loading } from '../Loading';

interface InputFileMediaProps extends InputHTMLAttributes<HTMLInputElement> {
  name: string;
  /** Endpoint usado para o PATCH (upload) e o DELETE (remoção). */
  endpoint: string;
  /** Nome do campo dentro do FormData enviado no upload. */
  formFieldName: string;
  /** Valor do header `identificator` esperado pelo backend. */
  identificator: string;
  /** Chave da resposta do PATCH que contém a nova URL da mídia. */
  mediaUrlKey: string;
  /**
   * O PATCH de logo da academia devolve só os dados do ginásio, não o usuário
   * inteiro — nesse caso é preciso buscar `users` de novo pra atualizar o
   * contexto de auth. O PATCH de avatar já devolve o usuário completo.
   */
  refetchUserAfterChange?: boolean;
  defaultFile?: string;
  defaultImage?: string;
  style?: any;
}

const InputFileMedia: React.FC<InputFileMediaProps> = ({
  name,
  endpoint,
  formFieldName,
  identificator,
  mediaUrlKey,
  refetchUserAfterChange = false,
  style,
  defaultFile,
  defaultImage = noMidiaDefault,
  ...rest
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const { addToast } = useToast();
  const { updateUser } = useAuth();
  const { addModal } = useModal();

  const [file, setFile] = useState<string | undefined>(defaultFile);
  const [loading, setLoading] = useState(false);

  const { defaultValue, fieldName, registerField } = useField(name);

  const supportedFileTypes = ['.jpg', '.jpeg', '.png'];

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: inputRef.current,
      path: 'value',
    });
  }, [fieldName, registerField]);

  useEffect(() => {
    setFile(defaultFile || undefined);
  }, [defaultFile]);

  const uploadFile = useCallback(
    async (croppedFile: File) => {
      setLoading(true);
      try {
        const data = new FormData();
        data.append(formFieldName, croppedFile, croppedFile.name);

        const response = await api.patch(endpoint, data, {
          headers: { identificator },
        });

        setFile(response.data[mediaUrlKey]);

        if (refetchUserAfterChange) {
          const userData = await api.get('users');
          updateUser(userData.data);
        } else {
          updateUser(response.data);
        }

        addToast({
          type: 'success',
          title: 'Upload realizado com sucesso.',
        });

        addToast({
          type: 'info',
          title: 'Caso a miniatura não atualize automaticamente',
          description: 'Faça o logoff e entre novamente na plataforma.',
        });

        setLoading(false);
      } catch (err: any) {
        setLoading(false);

        if (err?.response?.status === 510) {
          addToast({
            type: 'error',
            title: 'Erro na compressão da imagem',
            description:
              'Ocorreu um erro compressão da imagem. Tente novamente mais tarde.',
          });
          return;
        }

        if (err?.response?.status === 413) {
          addToast({
            type: 'error',
            title: 'Arquivo muito grande',
            description:
              'O arquivo enviado excede o limite permitido. Tente novamente com um arquivo menor.',
          });
          addToast({
            type: 'info',
            title: 'Permitidas imagens de até 5mb.',
          });

          return;
        }

        addToast({
          type: 'error',
          title: 'Erro na atualização',
          description:
            'Ocorreu um erro na atualização. Tente novamente mais tarde.',
        });
      }
    },
    [
      addToast,
      updateUser,
      endpoint,
      formFieldName,
      identificator,
      mediaUrlKey,
      refetchUserAfterChange,
    ],
  );

  const handleChangeFile = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const selectedFile = e.target.files?.[0];

      // Limpa o input logo em seguida -- se o recorte for cancelado, o
      // usuário precisa poder selecionar o mesmo arquivo de novo e o
      // evento `change` só dispara se o valor realmente mudar.
      e.target.value = '';

      if (!selectedFile) {
        return;
      }

      const [, type] = selectedFile.type.split('/');

      if (
        !supportedFileTypes.some(x =>
          x.toUpperCase().includes(type.toUpperCase()),
        )
      ) {
        addToast({
          type: 'error',
          title: `Arquivo ${type} não suportado`,
          description: `Os tipos de arquivos suportados são ${supportedFileTypes.join(
            ', ',
          )}`,
        });
        return;
      }

      addModal(<CropImageModal file={selectedFile} onConfirm={uploadFile} />);
    },
    [addModal, addToast, uploadFile],
  );

  const handleRemoveFile = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.delete(endpoint);

      setFile(undefined);

      if (refetchUserAfterChange) {
        const userData = await api.get('users');
        updateUser(userData.data);
      } else {
        updateUser(response.data);
      }

      addToast({
        type: 'success',
        title: 'Arquivo removido com sucesso.',
      });
      setLoading(false);
    } catch (err) {
      setLoading(false);
      addToast({
        type: 'error',
        title: 'Erro na atualização',
        description:
          'Ocorreu um erro na atualização. tente novamente mais tarde.',
      });
    }
  }, [addToast, updateUser, endpoint, refetchUserAfterChange]);

  return (
    <ContainerInput style={style}>
      <ContainerContainerMidia>
        <ContainerMidia>
          {loading ? (
            <Loading no_shadow={loading} />
          ) : (
            <img src={file || defaultImage} alt="midia exercise" />
          )}
        </ContainerMidia>
      </ContainerContainerMidia>

      <ContainerButton>
        <label htmlFor={`file-${name}`}>
          <Button
            name="changeImage"
            value={!file ? 'Enviar Imagem' : 'Alterar Imagem'}
            imageMobile={cameraImage}
          />
        </label>

        <Button
          name="removeImage"
          value="Remover Imagem"
          onClick={handleRemoveFile}
          imageMobile={trashImage}
          removeStyle
        />
      </ContainerButton>

      <Input
        id={`file-${name}`}
        type="file"
        accept={supportedFileTypes.join(',')}
        ref={inputRef}
        defaultValue={defaultValue}
        onChange={handleChangeFile}
        {...rest}
      />
    </ContainerInput>
  );
};

export { InputFileMedia };
